import React from 'react'
import { observer } from 'mobx-react-lite'

import Paper from '@mui/material/Paper'
import { DataGridPro, GridCellParams, GridColDef } from '@mui/x-data-grid-pro'

import {
  ANALYTICS_EVENTS,
  Loader,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
} from '../../../common'

import { useTransactionsUiStore } from '../../context/hooks'

import {
  AccountCell,
  AmountCell,
  CategoryCell,
  DateCell,
  FlagCell,
  HeaderCell,
  PayeeCell,
} from './transactiontable/cells'

interface TransactionTableProps {
  height?: number | string
  onCategoryClick?: (guid: string) => void
  onRowClick?: (guid: string) => void
}

const TransactionTable: React.FC<TransactionTableProps> = ({
  height = '100dvh',
  onCategoryClick,
  onRowClick,
}) => {
  const { onEvent } = useEvent()
  const { transactions: copy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()
  const { isLoading, sortedTransactions } = useTransactionStore()
  const { clearExpandedSplits, toggleSplit } = useTransactionsUiStore()

  React.useEffect(() => {
    return () => clearExpandedSplits()
  }, [])

  const rows = React.useMemo(
    () =>
      sortedTransactions.map((transaction) => ({
        id: transaction.guid,
        isHovering: false,
        ...transaction,
      })),
    [sortedTransactions],
  )

  const handleCellClick = ({ row: { id, guid, has_been_split }, field }: GridCellParams) => {
    if (has_been_split) return toggleSplit(id)

    if (field === 'category') {
      onEvent(ANALYTICS_EVENTS.TRANSACTIONS_TABLE_CLICK_CATEGORY, { guid })
      return onCategoryClick?.(guid)
    }

    return onRowClick?.(guid)
  }

  const handleCellKeyDown = (params: GridCellParams, event: React.KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    handleCellClick(params)
  }

  if (!isCopyLoaded) {
    return <Loader />
  }

  const columns: GridColDef[] = [
    {
      field: 'flag',
      headerName: '',
      minWidth: 32,
      width: 32,
      sortable: false,
      renderCell: (params) => <FlagCell {...params} />,
      renderHeader: () => <span aria-label={copy.transaction_status} />,
    },
    {
      field: 'date',
      headerName: copy.date_title,
      width: 103,
      renderHeader: HeaderCell,
      renderCell: (params) => (params.row.parent_guid ? null : <DateCell {...params} />),
      sortingOrder: ['desc', 'asc'],
    },
    {
      cellClassName: 'vertical-align-center',
      field: 'payee',
      headerName: copy.payee_title,
      flex: 0.33,
      renderHeader: HeaderCell,
      renderCell: (params) => <PayeeCell {...params} excludedText={copy.is_excluded} />,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'category',
      headerName: copy.category_title,
      flex: 0.25,
      renderHeader: HeaderCell,
      renderCell: (params) => <CategoryCell {...params} />,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'account',
      headerName: copy.account_title,
      flex: 0.33,
      renderHeader: HeaderCell,
      renderCell: (params) => (params.row.parent_guid ? null : <AccountCell {...params} />),
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'amount',
      headerName: copy.amount_title,
      flex: 0.25,
      renderHeader: HeaderCell,
      renderCell: AmountCell,
      align: 'right',
      headerAlign: 'right',
      sortingOrder: ['asc', 'desc'],
    },
  ]

  return (
    <Paper sx={{ height: height, px: 24, width: '100%', boxShadow: 'none' }}>
      <DataGridPro
        columns={columns}
        disableColumnFilter={true}
        disableColumnMenu={true}
        getCellClassName={(params) => {
          if (params.row.parent_guid) return 'split-row'
          if (params.row.is_hidden) return 'excluded-row'
          return ''
        }}
        hideFooter={true}
        initialState={{ sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
        loading={isLoading}
        onCellClick={handleCellClick}
        onCellKeyDown={handleCellKeyDown}
        rows={rows}
        slotProps={{ baseIconButton: { color: 'secondary', sx: { ml: 8 } } }}
        sx={{
          '& .split-row': {
            bgcolor: 'background.default',
          },
          '& .excluded-row': {
            bgcolor: 'grey.50',
            color: 'text.secondary',
          },
        }}
      />
    </Paper>
  )
}

export default observer(TransactionTable)
